/**
 * Stylized mockup of the NIS360 task board (kanban + progress) used as a
 * screenshot stand-in on marketing pages. Texts are intentionally hardcoded
 * German, exactly like a real product screenshot would be.
 */

interface MockupTask {
	title: string;
	priority: "Hoch" | "Mittel" | "Niedrig";
	initials: string;
}

const PRIORITY_STYLES: Record<MockupTask["priority"], string> = {
	Hoch: "bg-[#FFEAE0] text-[#D9580D]",
	Mittel: "bg-[#FFF6DA] text-[#A87B00]",
	Niedrig: "bg-[#E2ECFF] text-[#2F62D9]",
};

const COLUMNS: { name: string; count: number; tasks: MockupTask[] }[] = [
	{
		name: "Offen",
		count: 12,
		tasks: [
			{
				title: "Meldeprozess für Sicherheitsvorfälle definieren",
				priority: "Hoch",
				initials: "TK",
			},
			{ title: "Lieferanten-Risikobewertung durchführen", priority: "Mittel", initials: "SM" },
		],
	},
	{
		name: "In Arbeit",
		count: 6,
		tasks: [
			{ title: "Backup- & Recovery-Konzept dokumentieren", priority: "Hoch", initials: "JR" },
			{ title: "Multi-Faktor-Authentifizierung einführen", priority: "Mittel", initials: "TK" },
		],
	},
	{
		name: "Erledigt",
		count: 14,
		tasks: [
			{ title: "Betroffenheitsanalyse abgeschlossen", priority: "Niedrig", initials: "AM" },
			{ title: "Verantwortlichkeiten festgelegt", priority: "Mittel", initials: "SM" },
		],
	},
];

function TaskCard({ task }: { task: MockupTask }) {
	return (
		<div className="bg-white rounded-lg border border-[#E6E6EC] p-2.5 flex flex-col gap-2">
			<p className="font-urbanist text-[11px] font-medium text-[#1D243C] leading-[140%]">
				{task.title}
			</p>
			<div className="flex items-center justify-between">
				<span
					className={`font-urbanist text-[9px] font-semibold px-1.5 py-0.5 rounded ${
						PRIORITY_STYLES[task.priority]
					}`}
				>
					{task.priority}
				</span>
				<span className="w-5 h-5 rounded-full bg-[#1A2E6E] text-white text-[8px] font-semibold flex items-center justify-center">
					{task.initials}
				</span>
			</div>
		</div>
	);
}

export default function BoardMockup() {
	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-1.5">
				<div className="flex items-center justify-between">
					<p className="font-urbanist text-[13px] font-bold text-[#1D243C]">
						Ihr NIS-2-Maßnahmenplan
					</p>
					<p className="font-urbanist text-[11px] font-semibold text-[#2AA216]">
						14 von 32 Maßnahmen umgesetzt
					</p>
				</div>
				<div className="w-full h-2 bg-[#EDEFF5] rounded-full overflow-hidden">
					<div className="h-full w-[44%] bg-[#2AA216] rounded-full" />
				</div>
			</div>
			<div className="grid grid-cols-3 gap-2.5">
				{COLUMNS.map((column) => (
					<div key={column.name} className="bg-[#F7F8FB] rounded-xl p-2.5 flex flex-col gap-2">
						<div className="flex items-center justify-between px-0.5">
							<p className="font-urbanist text-[11px] font-bold text-[#1D243C]">{column.name}</p>
							<span className="font-urbanist text-[10px] font-semibold text-[#626373] bg-white border border-[#E6E6EC] rounded-full px-1.5">
								{column.count}
							</span>
						</div>
						{column.tasks.map((task) => (
							<TaskCard key={task.title} task={task} />
						))}
					</div>
				))}
			</div>
		</div>
	);
}
